import React from "react";
import { MapPin, Route, Compass, Clock } from "lucide-react";
import { SHOWCASE_IMAGES } from "../mock";

const points = [
  { icon: MapPin, title: "Smart pins", desc: "Every place, saved with context" },
  { icon: Route, title: "Route order", desc: "Your day, in the right sequence" },
  { icon: Compass, title: "Transport modes", desc: "Walk, drive, transit or ride" },
  { icon: Clock, title: "Timings", desc: "So no one gets left behind" },
];

const MapsShowcase = () => {
  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-[#f6f1e7] to-[#efe6d4]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-900/70 font-semibold mb-4">Maps, routes & movement</div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-emerald-950 leading-[1.05]">
              See the trip as a route, <span className="italic text-emerald-700">not a pile of notes.</span>
            </h2>
            <p className="mt-5 text-emerald-950/70 text-lg max-w-lg">
              Splitara keeps locations, transport modes, directions, and itinerary order connected — so travellers always know what happens next and where they need to be.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {points.map((p, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-black/5 hover:shadow-md transition">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 grid place-items-center shrink-0">
                    <p.icon className="w-5 h-5 text-emerald-800" />
                  </div>
                  <div>
                    <div className="font-semibold text-emerald-950">{p.title}</div>
                    <div className="text-sm text-emerald-950/60">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="order-1 lg:order-2 relative">
            <div className="relative rounded-[36px] overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={SHOWCASE_IMAGES.maps} alt="Trip map" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/60 via-transparent to-transparent" />
            </div>

            {/* Floating card */}
            <div className="floaty absolute -left-4 md:-left-8 bottom-16 bg-white rounded-2xl shadow-xl p-4 w-64 border border-black/5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-orange-100 grid place-items-center">
                  <MapPin className="w-4 h-4 text-orange-700" />
                </div>
                <div>
                  <div className="text-xs text-emerald-950/60">Next stop</div>
                  <div className="font-semibold text-emerald-950">Chefchaouen, Morocco</div>
                </div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-emerald-50 overflow-hidden">
                <div className="h-full w-2/3 bg-emerald-700 rounded-full" />
              </div>
              <div className="mt-2 flex justify-between text-xs text-emerald-950/60">
                <span>2h 15m drive</span>
                <span>4 stops today</span>
              </div>
            </div>

            <div className="floaty absolute -right-2 md:-right-6 top-10 bg-white rounded-2xl shadow-xl p-4 w-56 border border-black/5" style={{ animationDelay: "1.2s" }}>
              <div className="text-xs text-emerald-950/60 mb-1">Group balance</div>
              <div className="font-display text-2xl font-semibold text-emerald-900">€ 428.50</div>
              <div className="text-xs text-emerald-700 mt-1">All settled ✓</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MapsShowcase;
